import React, { useState, useEffect } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { clinicAPI, authAPI } from '../../../utils/api';
import { formatImageUrl } from '../../../utils/imageUtils';

const ClinicDoctors = () => {
    const { showSuccess, showError } = useNotification(); // Fixed: Removed unused showInfo
    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newDoctor, setNewDoctor] = useState({
        name: '',
        email: '',
        phone: '',
        specialization: '',
        experience: '',
        consultation_fee: '',
        profile_image: ''
    });
    const [uploading, setUploading] = useState(false);
    const [generatedSecret, setGeneratedSecret] = useState(null);

    useEffect(() => {
        fetchDoctors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchDoctors = async () => {
        try {
            const res = await clinicAPI.getDoctors();
            if (res.data?.success) {
                setDoctors(res.data.data || []);
            }
        } catch (error) {
            showError('Failed to fetch doctors');
        } finally {
            setLoading(false);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('profile_image', file);

        try {
            setUploading(true);
            const res = await authAPI.uploadProfileImage(formData);
            if (res.data.success) {
                setNewDoctor({ ...newDoctor, profile_image: res.data.data.filename });
                showSuccess('Image uploaded successfully');
            }
        } catch (error) {
            showError('Image upload failed');
        } finally {
            setUploading(false);
        }
    };

    const handleAddDoctor = async (e) => {
        e.preventDefault();
        if (!newDoctor.profile_image) {
            return showError('Please upload a profile image first');
        }
        try {
            const res = await clinicAPI.addDoctor(newDoctor);
            if (res.data?.success) {
                showSuccess('Doctor added successfully');
                setGeneratedSecret({
                    name: newDoctor.name,
                    secret_key: res.data.data.secretKey
                });
                setNewDoctor({
                    name: '',
                    email: '',
                    phone: '',
                    specialization: '',
                    experience: '',
                    consultation_fee: '',
                    profile_image: ''
                });
                setShowAddModal(false);
                fetchDoctors();
            } else {
                showError(res.data?.message || 'Failed to add doctor');
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Failed to add doctor');
        }
    };

    const handleRemoveDoctor = async (doctorId) => {
        if (!window.confirm('Are you sure you want to remove this doctor from the clinic?')) return;
        try {
            const res = await clinicAPI.removeDoctor(doctorId);
            if (res.data?.success) {
                showSuccess('Doctor removed successfully');
                fetchDoctors();
            } else {
                showError(res.data?.message || 'Failed to remove doctor');
            }
        } catch (error) {
            showError('Failed to remove doctor');
        }
    };

    if (loading) return <div className="text-center py-8">Loading doctors...</div>;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gold">Manage Doctors</h1>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="bg-gold text-dark px-4 py-2 rounded-lg font-medium"
                >
                    Add New Doctor
                </button>
            </div>

            {/* Secret Key Modal */}
            {generatedSecret && (
                <div className="bg-green-900 border border-green-500 rounded-xl p-6 mb-6">
                    <h3 className="text-xl font-bold text-white mb-2">Doctor Added Successfully!</h3>
                    <p className="text-green-200 mb-4">
                        Please share this Secret Key with <strong>{generatedSecret.name}</strong>.
                        They will use it to login for the first time.
                    </p>
                    <div className="bg-black p-4 rounded-lg flex justify-between items-center">
                        <code className="text-2xl font-mono text-gold">{generatedSecret.secret_key}</code>
                        <button
                            onClick={() => {
                                navigator.clipboard.writeText(generatedSecret.secret_key);
                                showSuccess('Copied to clipboard');
                            }}
                            className="text-gray-400 hover:text-white"
                        >
                            Copy
                        </button>
                    </div>
                    <button
                        onClick={() => setGeneratedSecret(null)}
                        className="mt-4 text-sm text-green-300 hover:text-white underline"
                    >
                        Dismiss
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {doctors.length === 0 ? (
                    <div className="col-span-full text-center py-8 text-gray-400">
                        No doctors found. Add your first doctor.
                    </div>
                ) : (
                    doctors.map((doctor) => (
                        <div key={doctor._id} className="bg-gray-800 rounded-xl p-6">
                            <div className="flex items-center gap-4 mb-4">
                                <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center text-xl font-bold text-gold overflow-hidden">
                                    {doctor.profile_image ? (
                                        <img src={formatImageUrl(doctor.profile_image)} alt={doctor.name} className="w-full h-full object-cover" />
                                    ) : (
                                        doctor.name.charAt(0)
                                    )}
                                </div>
                                <div>
                                    <h3 className="font-semibold text-light">{doctor.name}</h3>
                                    <p className="text-sm text-gray-400">{doctor.specialization}</p>
                                </div>
                            </div>
                            <div className="space-y-2 text-sm text-gray-400 mb-4">
                                <p>Email: {doctor.email}</p>
                                <p>Phone: {doctor.phone}</p>
                                <p>Experience: {doctor.experience} years</p>
                                <div className="flex justify-between items-center pt-2">
                                    <span>Status:</span>
                                    <span className={`px-2 py-0.5 rounded text-xs ${doctor.is_approved ? 'bg-green-600 text-white' : 'bg-yellow-600 text-dark'
                                        }`}>
                                        {doctor.is_approved ? 'Active' : 'Pending'}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => handleRemoveDoctor(doctor._id)}
                                className="w-full bg-red-900/50 text-red-300 py-2 rounded-lg hover:bg-red-900 transition"
                            >
                                Remove Doctor
                            </button>
                        </div>
                    ))
                )}
            </div>

            {/* Add Doctor Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
                        <h2 className="text-xl font-bold text-gold mb-4">Add New Doctor</h2>
                        <form onSubmit={handleAddDoctor} className="space-y-4">
                            <div>
                                <label className="block text-gray-400 text-sm mb-1">Name</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-light"
                                    value={newDoctor.name}
                                    onChange={(e) => setNewDoctor({ ...newDoctor, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-gray-400 text-sm mb-1">Email</label>
                                <input
                                    type="email"
                                    required
                                    className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-light"
                                    value={newDoctor.email}
                                    onChange={(e) => setNewDoctor({ ...newDoctor, email: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-gray-400 text-sm mb-1">Phone</label>
                                <input
                                    type="tel"
                                    required
                                    className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-light"
                                    value={newDoctor.phone}
                                    onChange={(e) => setNewDoctor({ ...newDoctor, phone: e.target.value })}
                                />
                            </div>
                            <div className="bg-gray-900/50 p-4 rounded-xl border border-dashed border-gray-700">
                                <label className="block text-gray-400 text-xs font-bold uppercase mb-3">Profile Image (Mandatory)</label>
                                <div className="flex items-center gap-4">
                                    <div className="w-16 h-16 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden border border-gray-600">
                                        {newDoctor.profile_image ? (
                                            <img src={formatImageUrl(newDoctor.profile_image)} alt="Preview" className="w-full h-full object-cover" />
                                        ) : (
                                            <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageUpload}
                                            className="hidden"
                                            id="doctor-image-upload"
                                        />
                                        <label
                                            htmlFor="doctor-image-upload"
                                            className={`inline-block px-4 py-2 rounded-lg text-sm font-bold cursor-pointer transition-colors ${uploading ? 'bg-gray-600' : 'bg-gold text-dark hover:bg-yellow-500'}`}
                                        >
                                            {uploading ? 'Uploading...' : 'Choose Image'}
                                        </label>
                                        <p className="text-[10px] text-gray-500 mt-1 italic">JPG/PNG, Max 2MB.</p>
                                    </div>
                                </div>
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setShowAddModal(false)}
                                    className="flex-1 bg-gray-700 text-light py-2 rounded hover:bg-gray-600"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={uploading}
                                    className={`flex-1 bg-gold text-dark py-2 rounded font-medium ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {uploading ? 'Uploading...' : 'Add Doctor'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClinicDoctors;
